'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackConversion, trackFunnelStage } from '@/lib/analytics';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  preferredContact: string;
  language: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    preferredContact: 'email',
    language: 'en',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'This field is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'This field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'This field is required';
    } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'This field is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      trackFunnelStage('form_validation_failed', {
        page: 'contact',
        language: 'en'
      });
      return;
    }

    // Strict runtime guard: Ensure Supabase credentials are configured
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      setErrors({
        submit: 'Configuration error: Missing Supabase credentials. Please contact support.'
      });
      setIsSubmitting(false);
      return;
    }

    trackFunnelStage('form_submission_started', {
      language: 'en'
    });

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-contact-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey || '',
            Authorization: `Bearer ${supabaseAnonKey || ''}`,
          },
          body: JSON.stringify({
            ...formData,
            language: 'en'
          }),
        }
      );

      // Check content-type before parsing JSON
      const contentType = response.headers.get('content-type') || '';
      
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        const preview = text.substring(0, 200);
        console.error('Non-JSON response:', { status: response.status, preview });
        throw new Error(
          `Server returned ${response.status} with non-JSON response. ` +
          `This usually means the API endpoint is not reachable. Please contact support.`
        );
      }

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to send message');
      }

      // Track successful conversion
      trackConversion({
        value: 0,
        currency: 'EUR'
      });

      trackFunnelStage('form_submission_success', {
        language: 'en'
      });

      setIsSubmitting(false);
      setSubmitSuccess(true);

      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          preferredContact: 'email',
          language: 'en',
        });
      }, 5000);
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      setErrors({ submit: errorMessage });
      
      trackFunnelStage('form_submission_failed', {
        error: errorMessage,
        language: 'en'
      });
      
      setTimeout(() => {
        setErrors({});
      }, 5000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (submitSuccess) {
    return (
      <div 
        className="backdrop-blur-sm rounded-lg p-8 border text-center"
        style={{
          backgroundColor: 'rgba(3, 27, 58, 0.3)',
          borderColor: 'rgba(22, 197, 95, 0.5)'
        }}
      >
        <div className="mb-6">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{
              backgroundColor: 'rgba(22, 197, 95, 0.2)'
            }}
          >
            <Icon name="CheckCircleIcon" size={32} variant="solid" style={{ color: '#16c55f' }} />
          </div>
          <h3 
            className="text-2xl font-bold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f'
            }}
          >
            Message Sent Successfully!
          </h3>
          <p 
            className="text-lg"
            style={{
              fontFamily: '"Inter", sans-serif',
              color: '#ffffff'
            }}
          >
            Thank you for reaching out. We'll respond within 24 hours.
          </p>
        </div>
        <button
          onClick={() => setSubmitSuccess(false)}
          className="px-6 py-3 rounded-lg font-semibold transition-all duration-300"
          style={{
            fontFamily: '"Exo 2", sans-serif',
            backgroundColor: 'transparent',
            border: '2px solid #00b8ff',
            color: '#00b8ff'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#00b8ff';
            e.currentTarget.style.color = '#031b3a';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#00b8ff';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <div 
      className="backdrop-blur-sm rounded-lg p-8 border"
      style={{
        backgroundColor: 'rgba(3, 27, 58, 0.3)',
        borderColor: 'rgba(0, 184, 255, 0.3)'
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label 
            htmlFor="name" 
            className="block text-sm font-semibold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
          >
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            className="w-full px-4 py-3 rounded-lg transition-all duration-300 focus:outline-none"
            style={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: 'rgba(3, 27, 58, 0.5)',
              border: errors.name ? '2px solid #ff4444' : '2px solid rgba(0, 184, 255, 0.3)',
              color: '#ffffff'
            }}
            onFocus={(e) => {
              if (!errors.name) {
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 184, 255, 0.3)';
              }
            }}
            onBlur={(e) => {
              if (!errors.name) {
                e.currentTarget.style.borderColor = 'rgba(0, 184, 255, 0.3)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
          {errors.name && (
            <p className="mt-1 text-sm" style={{ color: '#ff4444' }}>
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label 
            htmlFor="email" 
            className="block text-sm font-semibold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
          >
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            className="w-full px-4 py-3 rounded-lg transition-all duration-300 focus:outline-none"
            style={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: 'rgba(3, 27, 58, 0.5)',
              border: errors.email ? '2px solid #ff4444' : '2px solid rgba(0, 184, 255, 0.3)',
              color: '#ffffff'
            }}
            onFocus={(e) => {
              if (!errors.email) {
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 184, 255, 0.3)';
              }
            }}
            onBlur={(e) => {
              if (!errors.email) {
                e.currentTarget.style.borderColor = 'rgba(0, 184, 255, 0.3)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
          {errors.email && (
            <p className="mt-1 text-sm" style={{ color: '#ff4444' }}>
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label 
            htmlFor="phone" 
            className="block text-sm font-semibold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
          >
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+49 123 456 7890"
            className="w-full px-4 py-3 rounded-lg transition-all duration-300 focus:outline-none"
            style={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: 'rgba(3, 27, 58, 0.5)',
              border: errors.phone ? '2px solid #ff4444' : '2px solid rgba(0, 184, 255, 0.3)',
              color: '#ffffff'
            }}
            onFocus={(e) => {
              if (!errors.phone) {
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 184, 255, 0.3)';
              }
            }}
            onBlur={(e) => {
              if (!errors.phone) {
                e.currentTarget.style.borderColor = 'rgba(0, 184, 255, 0.3)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
          {errors.phone && (
            <p className="mt-1 text-sm" style={{ color: '#ff4444' }}>
              {errors.phone}
            </p>
          )}
        </div>

        {/* Company Field */}
        <div>
          <label 
            htmlFor="company" 
            className="block text-sm font-semibold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
          >
            Company Name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your Company Ltd."
            className="w-full px-4 py-3 rounded-lg transition-all duration-300 focus:outline-none"
            style={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: 'rgba(3, 27, 58, 0.5)',
              border: '2px solid rgba(0, 184, 255, 0.3)',
              color: '#ffffff'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#00b8ff';
              e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 184, 255, 0.3)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 184, 255, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Message Field */}
        <div>
          <label 
            htmlFor="message" 
            className="block text-sm font-semibold mb-2"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
          >
            Project Details *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your project, goals, and timeline..."
            rows={6}
            className="w-full px-4 py-3 rounded-lg transition-all duration-300 focus:outline-none resize-none"
            style={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: 'rgba(3, 27, 58, 0.5)',
              border: errors.message ? '2px solid #ff4444' : '2px solid rgba(0, 184, 255, 0.3)',
              color: '#ffffff'
            }}
            onFocus={(e) => {
              if (!errors.message) {
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 184, 255, 0.3)';
              }
            }}
            onBlur={(e) => {
              if (!errors.message) {
                e.currentTarget.style.borderColor = 'rgba(0, 184, 255, 0.3)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
          {errors.message && (
            <p className="mt-1 text-sm" style={{ color: '#ff4444' }}>
              {errors.message}
            </p>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div 
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
              borderColor: '#ff4444'
            }}
          >
            <p className="text-sm" style={{ color: '#ff4444' }}>
              {errors.submit}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-6 py-4 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            fontFamily: '"Exo 2", sans-serif',
            backgroundColor: 'transparent',
            border: '2px solid #00b8ff',
            color: '#00b8ff'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = '#00b8ff';
              e.currentTarget.style.color = '#031b3a';
              e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#00b8ff';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin mr-2" />
              Sending...
            </span>
          ) : (
            'Send Message'
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;